/**
 * Colours of the CityDrinks Club prototype, as Tailwind arbitrary values local
 * to the membership module: no global token is added for them.
 */
export const CLUB_CLASS = {
  /** The site's text font with the prototype's antialiased smoothing: `body` sets neither, so every club surface sets it on its root. */
  font: '[font-family:var(--fontFamily2)] antialiased',
  /** Burgundy gradient of banners, the desktop hero and the cart and checkout rows. */
  burgundy: 'bg-[linear-gradient(120deg,#8B1235_0%,#3C0A17_100%)]',
  /** Hero gradient: mobile pink-to-burgundy, the burgundy one from desktop. */
  hero: 'bg-[linear-gradient(90deg,#d8184e_-5.45%,#681432_108.96%)] desktop:bg-[linear-gradient(120deg,#8B1235_0%,#3C0A17_100%)]',
  /** Promo card of the mobile profile. */
  promo: 'bg-[linear-gradient(100deg,#d8184e_0%,#a8145a_62%,#681432_100%)]',
  /** Orange gradient of a member's account button and profile tab dot. */
  orange: 'bg-[linear-gradient(100deg,#e8552b_0%,#d2286a_60%,#a8145a_100%)]',
  /**
   * A club bottom sheet is the centred desktop card from `md` on: between
   * `md` and `desktop` the `Modal` still pins a bottom sheet to the bottom,
   * so the card is centred here with `translate` (the `Modal`'s own centring
   * transform only starts at `desktop`).
   */
  sheetCard:
    'md:max-desktop:top-1/2! md:max-desktop:bottom-auto! md:max-desktop:left-1/2! md:max-desktop:-translate-1/2!',
  /** Background of the dark club modals, on the `Modal` body (overrides its own). */
  modal: 'bg-[#3C0919]!',
  /** Benefit card inside a dark modal. */
  modalCard:
    'rounded-[20px] border border-[#612A3B] bg-[#ffffff14] px-4 py-3.5',
  /** Light card of the club page. */
  card: 'rounded-[20px] bg-[#f7f6f6]',
  /** Primary pill of the modals. */
  primary:
    'flex h-11 w-full cursor-pointer items-center justify-center gap-2 rounded-full border-0 bg-[#e31b54] [font-family:inherit] px-6 py-0 font-medium text-[16px] text-white leading-5 transition-colors duration-200 ease-[ease] hover:bg-[#c01048] disabled:cursor-default disabled:bg-[#0000010f] disabled:text-[#00000059]',
  /** White pill on a gradient. */
  white:
    'flex cursor-pointer items-center justify-center rounded-full border-0 bg-white px-8 py-0 [font-family:inherit] font-semibold text-[#141415] text-[16px] leading-6 transition-colors duration-200 ease-[ease] hover:bg-[#f0eeee]',
  /** Outlined pill on a light background. */
  outline:
    'flex cursor-pointer items-center justify-center rounded-full border border-[#2d2f332b] bg-white px-6 py-0 [font-family:inherit] font-semibold text-[#141415] text-[16px] leading-5 transition-colors duration-200 ease-[ease] hover:border-[#e31b54] hover:text-[#e31b54] disabled:cursor-default disabled:opacity-50',
  /** Headings: the site heading font, as the prototype's Fragment. */
  heading:
    'font-customer-heading font-medium [font-variant-numeric:lining-nums]',
  /** Big translucent threshold figure of a hero. */
  figure:
    'pointer-events-none absolute select-none font-semibold text-[#ffffff14] leading-none tracking-[-0.06em]',
  /** "Active" badge. */
  badge:
    'inline-flex h-6 items-center rounded-[12px] bg-[#0676471a] px-2 antialiased font-medium text-[#388b66] text-[12px] leading-[14px]',
  /** Secondary text on a light background. */
  muted: 'text-[#000000a3]',
  /** Secondary text on a dark or gradient background. */
  mutedOnDark: 'text-[#ffffffb2]',
  /** Club accent text (links, the cart row). */
  accentText: 'text-[#e31b54]',
  /** Club accent fill (small pills, ticks). */
  accentBg: 'bg-[#e31b54]',
  /** Label of the club discount row in the cart, checkout and order totals. */
  discountLabel: 'text-[#c01048]!',
} as const;
